// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MAX_COMMENT_CHARS } from "@/features/assistant/types";
import { cn } from "@/lib/utils";

/** Stable English values: the server receives these as reasons; the UI translates only the display (key in `i18nKey`). */
const CHIPS = {
  down: [
    { reason: "Wrong data", i18nKey: "wrongData" },
    { reason: "Wrong store or chain", i18nKey: "wrongScope" },
    { reason: "Incomplete", i18nKey: "incomplete" },
    { reason: "Didn't understand the question", i18nKey: "misunderstood" },
    { reason: "Too slow", i18nKey: "tooSlow" },
  ],
  up: [
    { reason: "Accurate", i18nKey: "accurate" },
    { reason: "Fast", i18nKey: "fast" },
    { reason: "Saved me time", i18nKey: "savedTime" },
  ],
} as const;

export function FeedbackDetails({
  feedback,
  feedbackState,
  onSendComment,
  onRetryFeedback,
}: {
  feedback: "up" | "down" | null;
  feedbackState: "sending" | "sent" | "failed" | null | undefined;
  onSendComment: (reasons: string[], comment: string) => Promise<void>;
  onRetryFeedback: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [commentFailed, setCommentFailed] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [text, setText] = useState("");

  const lineRef = useRef<HTMLParagraphElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const focusLine = useRef(false);

  // Keep focus out of <body>: into the textarea on open, back to the line when the form closes.
  useEffect(() => {
    if (open) textRef.current?.focus();
    else if (focusLine.current) {
      focusLine.current = false;
      (addRef.current ?? lineRef.current)?.focus();
    }
  }, [open]);

  // Chips are per rating: a switch clears them (typed text stays); un-rating closes the form.
  useEffect(() => {
    setSelected([]);
    if (feedback === null) setOpen(false);
  }, [feedback]);

  if (feedback === null) return null;

  if (feedbackState === "failed") {
    return (
      <p role="status" className="mb-1 text-muted-foreground text-xs">
        {t("assistant.feedback.failed")}{" "}
        <button type="button" className="underline underline-offset-2" onClick={onRetryFeedback}>
          {t("assistant.feedback.retry")}
        </button>
      </p>
    );
  }
  if (feedbackState !== "sent") return null;

  async function send() {
    setSending(true);
    setCommentFailed(false);
    try {
      await onSendComment(selected, text.trim());
      setDone(true);
      focusLine.current = true;
      setOpen(false);
    } catch {
      setCommentFailed(true);
    } finally {
      setSending(false);
    }
  }

  const toggle = (reason: string) =>
    setSelected((s) => (s.includes(reason) ? s.filter((r) => r !== reason) : [...s, reason]));

  if (!open) {
    return (
      <p ref={lineRef} tabIndex={-1} role="status" className="mb-1 text-muted-foreground text-xs outline-none">
        {t("assistant.feedback.thanks")}
        {!done && (
          <>
            {" "}
            <button ref={addRef} type="button" className="underline underline-offset-2" onClick={() => setOpen(true)}>
              {t("assistant.feedback.addDetails")}
            </button>
          </>
        )}
      </p>
    );
  }

  return (
    <div className="mb-1 space-y-2 text-xs">
      <div className="flex flex-wrap gap-1.5">
        {CHIPS[feedback].map(({ reason, i18nKey }) => {
          const on = selected.includes(reason);
          return (
            <button
              key={reason}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(reason)}
              className={cn(
                "rounded-full border px-2.5 py-1 transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                on ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted",
              )}
            >
              {t(`assistant.feedback.chips.${feedback}.${i18nKey}`)}
            </button>
          );
        })}
      </div>
      <Textarea
        ref={textRef}
        aria-label={t("assistant.feedback.placeholder")}
        placeholder={t("assistant.feedback.placeholder")}
        className="min-h-16 text-sm md:text-sm"
        maxLength={MAX_COMMENT_CHARS}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {commentFailed && (
        <p className="text-muted-foreground">
          {t("assistant.feedback.failed")}{" "}
          <button type="button" className="underline underline-offset-2" onClick={send}>
            {t("assistant.feedback.retry")}
          </button>
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            focusLine.current = true;
            setOpen(false);
          }}
        >
          {t("assistant.feedback.skip")}
        </Button>
        <Button size="sm" disabled={sending || (selected.length === 0 && text.trim() === "")} onClick={send}>
          {t("assistant.feedback.send")}
        </Button>
      </div>
    </div>
  );
}
